import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { Annotation } from '@shared/types';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';

interface AnnotatedBodyProps {
  html: string;
  annotations: Annotation[];
  className?: string;
  style?: React.CSSProperties;
}

interface OpenNote {
  html: string;
  /** Viewport coordinates of the anchor, resolved at open time. */
  top: number;
  left: number;
  width: number;
}

/** Roughly the popover's width; used to keep it inside the viewport. */
const POPOVER_WIDTH = 320;
const EDGE_GAP = 12;

/**
 * The piece, with the writer's notes on it.
 *
 * The body is injected as HTML — sanitised server-side on every write, which is
 * what makes that safe — so the annotation anchors inside it are plain DOM that
 * React never rendered and cannot hold refs to. The notes are therefore wired by
 * delegation from the container: one listener, and it keeps working when the
 * reader turns a page and the whole body is replaced.
 */
export function AnnotatedBody({ html, annotations, className, style }: AnnotatedBodyProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<OpenNote | null>(null);

  /*
   * Marks the anchors that actually resolve.
   *
   * An anchor whose note is missing — a page whose annotations did not arrive, a
   * post edited by an older client — is stripped of the class rather than left
   * underlined. A reader tapping an underline and being shown nothing is worse
   * than never being invited to tap.
   */
  useEffect(() => {
    const container = bodyRef.current;
    if (!container) return;

    const notes = new Map(annotations.map((note) => [note.id, note.html]));
    for (const anchor of container.querySelectorAll<HTMLElement>('span.annot')) {
      if (notes.has(anchor.id)) {
        anchor.setAttribute('role', 'button');
        anchor.setAttribute('tabindex', '0');
        anchor.setAttribute('aria-label', t('लेखकको टिप्पणी'));
      } else {
        anchor.classList.remove('annot');
      }
    }
  }, [html, annotations]);

  useEffect(() => {
    const container = bodyRef.current;
    if (!container) return;

    const notes = new Map(annotations.map((note) => [note.id, note.html]));

    const show = (anchor: HTMLElement) => {
      const note = notes.get(anchor.id);
      if (!note) return;
      const rect = anchor.getBoundingClientRect();
      setOpen({ html: note, top: rect.bottom, left: rect.left, width: rect.width });
    };

    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest<HTMLElement>('span.annot');
      if (!anchor || !container.contains(anchor)) return;
      // A note inside a link must not also follow the link.
      event.preventDefault();
      show(anchor);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const anchor = (event.target as HTMLElement | null)?.closest<HTMLElement>('span.annot');
      if (!anchor || !container.contains(anchor)) return;
      event.preventDefault();
      show(anchor);
    };

    container.addEventListener('click', onClick);
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('click', onClick);
      container.removeEventListener('keydown', onKeyDown);
    };
  }, [html, annotations]);

  /*
   * The popover is positioned from viewport coordinates, so anything that moves
   * the anchor invalidates it. Rather than track and re-measure — which fights
   * every scroll frame for a panel the reader is done with anyway — it closes.
   */
  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <div
        ref={bodyRef}
        className={cn('literary-body np mx-auto', className)}
        style={style}
        /*
          Sanitised server-side on every write (see worker/lib/html.ts), which is
          what makes this render safe — never trust the client for that.
        */
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {open && (
        <>
          {/* Catches the click that dismisses. Transparent rather than dimmed:
              the note is a footnote, not a modal, and the page behind it should
              not go dark to show three lines of it. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(null)} aria-hidden="true" />

          <div
            role="dialog"
            aria-label={t('लेखकको टिप्पणी')}
            className="annot-note fixed z-50 rounded-lg border bg-popover p-3 pe-8 shadow-lg"
            style={{
              top: Math.round(open.top + 8),
              // Centred under the anchor, then pulled back inside the viewport —
              // an annotated word at the right margin would otherwise open a
              // panel half of which is off the screen.
              left: Math.round(
                Math.min(
                  Math.max(open.left + open.width / 2 - POPOVER_WIDTH / 2, EDGE_GAP),
                  Math.max(window.innerWidth - POPOVER_WIDTH - EDGE_GAP, EDGE_GAP),
                ),
              ),
              width: Math.min(POPOVER_WIDTH, window.innerWidth - EDGE_GAP * 2),
            }}
          >
            <button
              type="button"
              className="absolute end-1.5 top-1.5 rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setOpen(null)}
              aria-label={t('बन्द')}
            >
              <X className="size-3.5" />
            </button>

            <p className="kicker np mb-1 text-primary">{t('लेखकको टिप्पणी')}</p>
            {/* Sanitised on write, exactly as the body is. */}
            <div className="np text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: open.html }} />
          </div>
        </>
      )}
    </>
  );
}
